import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import LoadingSequence from "@/components/LoadingSequence";
import ChannelHeader from "@/components/ChannelHeader";
import ChannelIntelligence from "@/components/ChannelIntelligence";
import StatsGrid from "@/components/StatsGrid";
import RevenuePanel from "@/components/RevenuePanel";
import ChannelScore from "@/components/ChannelScore";
import GrowthCharts from "@/components/GrowthCharts";
import VideosTable from "@/components/VideosTable";
import AIDiagnosis from "@/components/AIDiagnosis";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { analyzeChannel, getDiagnosis, getRankings } from "@/lib/api";
import { formatCompact } from "@/lib/format";

export default function ChannelPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    const pollDiagnosis = async (channelId) => {
      const startedAt = Date.now();
      const maxWaitMs = 3 * 60 * 1000; // stop after 3 minutes
      const tick = async () => {
        if (cancelled) return;
        try {
          const res = await getDiagnosis(channelId);
          if (cancelled) return;
          if (res.status === "ready" || res.status === "failed") {
            setData((d) => (d ? { ...d, diagnosis: res.diagnosis, diagnosis_status: res.status } : d));
            return;
          }
        } catch (e) {
          // keep polling even on transient errors
        }
        if (Date.now() - startedAt > maxWaitMs) return;
        pollTimer = setTimeout(tick, 2500);
      };
      tick();
    };

    const run = async () => {
      setLoading(true);
      try {
        const url = slug.startsWith("UC")
          ? `https://youtube.com/channel/${slug}`
          : `https://youtube.com/@${slug.replace(/^@/, "")}`;
        const result = await analyzeChannel(url);
        if (!cancelled) setData(result);
        // Start polling if the AI diagnosis is still generating.
        if (!cancelled && result && result.diagnosis_status !== "ready" && result.channel_id) {
          pollDiagnosis(result.channel_id);
        }
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.detail || e?.message || "Failed to load channel";
          setError(msg);
          toast.error(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    getRankings("subscribers")
      .then((rows) => setRelated(rows.filter((r) => r.channel_id !== slug && (r.title || "").toLowerCase() !== slug.toLowerCase()).slice(0, 6)))
      .catch((err) => console.debug("related rankings fetch failed", err));
    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [slug]);

  if (loading) return <LoadingSequence />;

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-16">
        <div className="p-6 border border-destructive/40 bg-destructive/10 rounded-xl text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;
  const s = data.stats;
  const title = `${s.title} — YouTube Channel Statistics, Revenue & AI Analysis`;
  const description = `Complete analysis of ${s.title}: ${formatCompact(s.subscribers)} subscribers, ${formatCompact(s.total_views)} total views, ${s.video_count} videos, ${s.engagement_rate}% engagement, channel score ${data.score.overall}/100. Statistics, revenue estimate, growth and AI recommendations.`;

  const jsonLd = [
    {
      "@type": "Article",
      headline: title,
      description,
      datePublished: data.cached_at,
      dateModified: data.cached_at,
      image: s.thumbnail,
      author: { "@type": "Organization", name: "Creator Intelligence Tube" },
      publisher: { "@type": "Organization", name: "Creator Intelligence Tube" },
      about: { "@type": "Person", name: s.title, url: `https://youtube.com/channel/${s.id}` },
    },
    {
      "@type": "Dataset",
      name: `${s.title} — YouTube Channel Statistics`,
      description: `Public statistics for the YouTube channel ${s.title}: subscribers, views, engagement, revenue and more.`,
      creator: { "@type": "Organization", name: "Creator Intelligence Tube" },
      variableMeasured: ["subscribers", "views", "engagement_rate", "revenue_estimate", "channel_score"],
    },
  ];

  return (
    <>
      <SEO
        title={title}
        description={description}
        path={`/channel/${slug}`}
        image={s.thumbnail}
        type="article"
        keywords={`${s.title}, ${s.title} youtube analytics, ${s.title} statistics, ${s.title} revenue, ${s.title} subscribers, youtube channel analyzer, youtube analytics tool, creator analytics, youtube monetization`}
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: "Rankings", path: "/rankings" },
          { name: s.title, path: `/channel/${slug}` },
        ]}
      />
      <div className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <Breadcrumbs items={[{ name: "Analyze Channel", path: "/analyze" }, { name: s.title, path: `/channel/${slug}` }]} />
        </div>
        <ChannelHeader stats={s} />

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <article className="card-flat p-6 sm:p-8 max-w-4xl">
            <h2 className="text-2xl font-bold tracking-tight">
              About the {s.title} channel analysis
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              This page presents a <strong>complete analysis of the YouTube channel {s.title}</strong>, including{" "}
              <strong>up-to-date statistics</strong>, an <strong>estimated revenue</strong> based on average CPM,
              growth charts, a table of the most recent videos with engagement metrics and a{" "}
              <strong>consultative AI diagnosis</strong> powered by GPT-5.2.
            </p>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              The channel currently has <strong className="text-foreground font-mono">{formatCompact(s.subscribers)} subscribers</strong>,{" "}
              <strong className="text-foreground font-mono">{formatCompact(s.total_views)} total views</strong>, and has published{" "}
              <strong className="text-foreground font-mono">{s.video_count} videos</strong>. Average views per video are{" "}
              <strong className="text-foreground font-mono">{formatCompact(s.avg_views)}</strong>, with an average engagement rate of{" "}
              <strong className="text-foreground font-mono">{s.engagement_rate}%</strong>. Our proprietary <strong>channel score</strong>,
              calculated from growth, consistency, frequency, engagement, SEO, performance and quality, stands at{" "}
              <strong className="text-primary font-mono">{data.score.overall}/100</strong>.
            </p>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Below you'll find the detailed <strong>YouTube channel statistics</strong>, the{" "}
              <strong>YouTube revenue calculator</strong> with low, average and high CPM scenarios,
              growth charts, the videos table and the full AI report with strengths, weaknesses,
              growth opportunities and monetization potential.
            </p>
          </article>
        </section>

        <StatsGrid stats={s} />
        <ChannelIntelligence stats={s} />
        <RevenuePanel revenue={data.revenue} />
        <ChannelScore score={data.score} />
        <GrowthCharts growth={data.growth} />
        <VideosTable videos={data.videos} />
        <AIDiagnosis diagnosis={data.diagnosis} status={data.diagnosis_status} />

        {related.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="overline">Related channels</div>
                <h2 className="mt-2 text-2xl font-extrabold tracking-tight">Explore similar creators</h2>
              </div>
              <Link to="/rankings" className="text-sm text-primary font-semibold hover:underline">
                See all rankings →
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {related.map((r) => (
                <Link
                  key={r.channel_id}
                  to={`/channel/${r.channel_id}`}
                  className="card-flat card-hover p-4 flex flex-col items-center text-center group"
                  data-testid={`related-${r.channel_id}`}
                >
                  {r.thumbnail && (
                    <img src={r.thumbnail} alt={`${r.title} avatar`} loading="lazy" className="w-14 h-14 rounded-full border border-border" />
                  )}
                  <div className="mt-3 font-bold text-xs truncate w-full group-hover:text-primary transition-colors">{r.title}</div>
                  <div className="mt-1 text-[10px] text-muted-foreground font-mono">{formatCompact(r.subscribers)} subs</div>
                </Link>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-2 text-xs">
              <span className="text-muted-foreground font-mono uppercase tracking-widest mr-2">More tools:</span>
              <Link to="/compare" className="px-3 py-1.5 rounded-full border border-border hover:border-primary hover:text-primary">Compare channels</Link>
              <Link to="/analyze" className="px-3 py-1.5 rounded-full border border-border hover:border-primary hover:text-primary">Analyze another channel</Link>
              <Link to="/rankings" className="px-3 py-1.5 rounded-full border border-border hover:border-primary hover:text-primary">Top rankings</Link>
              <Link to="/blog" className="px-3 py-1.5 rounded-full border border-border hover:border-primary hover:text-primary">Growth articles</Link>
            </div>
          </section>
        )}
      </div>
    </>
  );
}
